import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Injector, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlContainer, FormBuilder, NgControl, ReactiveFormsModule } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, FormGroupComponent, ModalService, SelectComponent } from 'mbs-ui-kit';
import { AddCompanyModalComponent } from 'mbs-ui-shared@components/add-company/add-company-modal.component';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { Company } from 'mbs-ui-shared@models';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PagedSelectComponent } from './paged-select.component';

@UntilDestroy()
@Component({
  selector: 'mbs-company-select',
  templateUrl: './company-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    ButtonComponent,
    FormGroupComponent,
    CanPipe,
    I18NextPipe,
    SelectComponent
  ]
})
export class CompanySelectComponent extends PagedSelectComponent<Company> {
  controlContainer: ControlContainer;
  protected ngControl: NgControl;
  private facade = inject(CompaniesFacade);
  private injector = inject(Injector);
  protected fb: FormBuilder;
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);

  private facadeData$ = toObservable(this.facade.data);
  private facadeDataTotal$ = toObservable(this.facade.dataTotal);
  private facadeAllLoaded$ = toObservable(this.facade.allDataLoaded);
  private facadeDataHasError$ = toObservable(this.facade.dataHasError);
  private facadeDataLoading$ = toObservable(this.facade.dataLoading);
  private facadeLoading$ = toObservable(this.facade.loading);

  protected bindLabel = 'name';

  public counterLabel = input<string>(this.i18nPipe.transform('shared:captions.companies'));
  public placeholder = input<string>(this.i18nPipe.transform('shared:placeholders.companies'));

  constructor() {
    const controlContainer = inject(ControlContainer, { optional: true, host: true, skipSelf: true });
    const ngControl = inject(NgControl, { optional: true, self: true });
    const fb = inject(FormBuilder);

    super();

    this.controlContainer = controlContainer;
    this.ngControl = ngControl;
    this.fb = fb;
  }

  protected loadData(): void {
    this.facade.loadData(false, true);
  }

  protected get getData$(): Observable<Company[]> {
    return this.facadeData$;
  }

  protected loadByIds(ids: string[]): void {
    this.facade.loadByIds({ ids });
  }

  protected getByIds$(ids: string[]): Observable<Company[]> {
    return toObservable(this.facade.entitiesById(ids), { injector: this.injector });
  }

  protected get getTotal$(): Observable<number> {
    return this.facadeDataTotal$;
  }

  protected get allLoaded$(): Observable<boolean> {
    return this.facadeAllLoaded$;
  }

  protected get dataHasError$(): Observable<boolean> {
    return this.facadeDataHasError$;
  }

  protected get loadingData$(): Observable<boolean> {
    return this.facadeDataLoading$;
  }

  protected get loadingById$(): Observable<boolean> {
    return this.facadeLoading$;
  }

  protected areItemsTheSame(selectedItem: Company, item: Company): boolean {
    return selectedItem?.id === item?.id;
  }

  public onAddCompanyButtonClick() {
    from(this.modalService.openCustom(AddCompanyModalComponent))
      .pipe(catchError(() => EMPTY))
      .subscribe((company) => {
        const patch = this.multiple() ? ([...((this.value() || []) as Company[]), company] as Company[]) : (company as Company);

        this.control.patchValue(patch);
      });
  }
}
